import React, { useState } from 'react';
import { useReports } from './ReportsContext';
import { useAppContext } from '../../libs/contextLib';
import { Card, Form, Input, DatePicker, Upload, Button, Table, Space, Spin, Modal, Progress, Typography, Select, Alert, Radio, Tag } from 'antd';
import { UploadOutlined, DeleteOutlined, DownloadOutlined, InboxOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from '../../dayjs';

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

// Get just the base file name without extension
const getBaseFileName = (fileName) => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  if (parts.length <= 1) return fileName;
  parts.pop(); // Remove extension
  return parts.join('.');
};

// Helper to parse date from filename if possible (e.g. 12870_20241231_...)
const parseDateFromFileName = (fileName) => {
  if (!fileName) return null;
  
  // Look for YYYYMMDD pattern in filename
  const datePattern = /[_-]?(\d{8})[_-]?/;
  const match = fileName.match(datePattern);
  
  if (match && match[1]) {
    const dateStr = match[1];
    // Try to parse YYYYMMDD format
    try {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return dayjs(`${year}-${month}-${day}`);
    } catch (e) {
      return null;
    }
  }
  
  return null;
};

export default function Admin() {
  const { messageApi } = useAppContext();
  const {
    reports = [],
    isLoading,
    uploadProgress,
    uploadReport,
    deleteReport,
    downloadReport,
    fetchReports,
    reportTypes = [],
  } = useReports();

  // File upload state
  const [fileList, setFileList] = useState([]);
  const [reportDate, setReportDate] = useState(dayjs());
  const [reportName, setReportName] = useState('');
  const [reportType, setReportType] = useState('otr');
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileType, setFileType] = useState('csv');
  const [uploadResults, setUploadResults] = useState([]);
  const [fileUploadProgress, setFileUploadProgress] = useState({ current: 0, total: 0 });
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  // Table columns for the reports list
  const columns = [
    {
      title: 'Download Date',
      dataIndex: 'reportDate',
      key: 'reportDate',
      render: date => date ? dayjs(date).format('YYYY-MM-DD') : 'Unknown',
      sorter: (a, b) => new Date(b.reportDate || 0) - new Date(a.reportDate || 0),
    },
    {
      title: 'Name',
      dataIndex: 'reportName',
      key: 'reportName',
      render: (text, record) => (
        <span>{text || `Report ${record.reportId ? record.reportId.substring(0, 8) : ''}`}</span>
      ),
    },
    {
      title: 'Reporting Dates',
      dataIndex: 'reportDates',
      key: 'reportDates',
      render: (text, record) => {
        if(record.reportDates?.length) {
          return record.reportDates.join(', ');
        }
        return 'Unknown';
      },
    },
    {
      title: 'Type',
      dataIndex: 'reportType',
      key: 'reportType',
      render: type => {
        const reportType = Array.isArray(reportTypes) 
          ? reportTypes.find(t => t.id === type) 
          : null;
        return reportType ? reportType.name : type;
      },
    },
    {
      title: 'File Type',
      dataIndex: 'fileType',
      key: 'fileType',
      render: fileType => fileType || 'csv',
    },
    {
      title: 'Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (_, record) => record && record.getFormattedSize ? record.getFormattedSize() : '',
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
    },
    {
      title: 'Uploaded At',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      render: date => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '',
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            size="small"
            onClick={() => downloadReport(record)}
            disabled={!record.fileKey}
          >
            Download
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            onClick={() => showDeleteConfirm(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    }
  ];

  // Reset form state for new upload
  const resetUploadForm = () => {
    setFileList([]);
    setReportName('');
    setReportDate(dayjs());
    setFileType('csv');
  };

  // File upload props
  const uploadProps = {
    name: 'file',
    multiple: true,
    accept: '.csv,.xlsx,.txt',
    maxCount: 100,
    fileList,
    beforeUpload: (file) => {
      // Get file extension
      const fileName = file.name;
      const extension = fileName.split('.').pop().toLowerCase();
      
      // Only allow certain file types
      const allowedTypes = ['csv', 'xlsx', 'txt'];
      if (!allowedTypes.includes(extension)) {
        Modal.error({
          title: 'Upload Error',
          content: 'Only CSV, Excel, and TXT files are supported!',
        });
        return false;
      }
      
      // In multiple file mode, add to existing files
      setFileList(prev => [...prev, file]);
      
      return false; // Prevent auto upload
    },
    onRemove: (file) => {
      setFileList(fileList.filter(item => item.uid !== file.uid));
    },
  };

  // Process files in batches with limited concurrency
  const processBatch = async (files, maxConcurrent = 5) => {
    const results = [];
    const total = files.length;
    let completed = 0;
    
    // Process files in chunks to limit concurrency
    for (let i = 0; i < files.length; i += maxConcurrent) {
      const batch = files.slice(i, i + maxConcurrent);
      
      // Process this batch in parallel
      const batchPromises = batch.map(file => {
        // Parse date from filename for each file
        const parsedDate = parseDateFromFileName(file.name) || reportDate;
        const baseName = getBaseFileName(file.name);
        const extension = file.name.split('.').pop().toLowerCase();
        
        return uploadReport(file, parsedDate, baseName, reportType, extension)
          .then(result => {
            completed++;
            setFileUploadProgress({ current: completed, total });
            return { file: file.name, success: true, result };
          })
          .catch(error => {
            completed++;
            setFileUploadProgress({ current: completed, total });
            return { file: file.name, success: false, error: error.message || 'Upload failed' };
          });
      });
      
      // Wait for this batch to complete
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }
    
    return results;
  };

  // Handle upload button click
  const handleUpload = async () => {
    if (fileList.length === 0) {
      Modal.error({
        title: 'Upload Error',
        content: 'Please select at least one file to upload',
      });
      return;
    }

    setUploading(true);
    setUploadResults([]);
    setFileUploadProgress({ current: 0, total: fileList.length });
    
    try {
      // Process all files in efficient batches
      const results = await processBatch(fileList);
      setUploadResults(results);
      
      // Display summary
      const successCount = results.filter(r => r.success).length;
      messageApi?.info(`Completed uploading ${successCount} of ${results.length} files`);
      
      // Reset form after successful upload(s)
      resetUploadForm();
      
      // Only close the modal if there were no issues
      if (successCount === results.length) {
        setTimeout(() => setShowUploadModal(false), 2000);
      }
    } finally {
      setUploading(false);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirm = (report) => {
    if (!report) return;
    
    confirm({
      title: 'Are you sure you want to delete this report?',
      icon: <ExclamationCircleOutlined />,
      content: `This action will permanently delete "${report.reportName || 'this report'}" and cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: () => {
        deleteReport(report.reportId);
      }
    });
  };
  
  // Show delete all confirmation modal
  const showDeleteAllConfirm = () => {
    const reportCount = Array.isArray(reports) ? reports.length : 0;
    if (reportCount === 0) {
      messageApi?.info('No reports to delete');
      return;
    }
    
    confirm({
      title: 'Delete All Reports',
      icon: <ExclamationCircleOutlined />,
      content: `This action will permanently delete all ${reportCount} reports and cannot be undone.`,
      okText: 'Yes, Delete All',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        setIsDeletingAll(true);
        try {
          // Delete each report
          const promises = reports.map(report => deleteReport(report.reportId, true));
          await Promise.all(promises);
          messageApi?.success(`Successfully deleted ${reportCount} reports`);
          // Refresh the report list
          fetchReports();
        } catch (error) {
          messageApi?.error('Failed to delete all reports');
          console.error('Error deleting all reports:', error);
        } finally {
          setIsDeletingAll(false);
        }
      }
    });
  };
  
  // Get sorted reports (safely)
  const getSortedReports = () => {
    if (!Array.isArray(reports)) return [];
    
    return [...reports].sort((a, b) => {
      if (!a.reportDate) return 1;
      if (!b.reportDate) return -1;
      return new Date(b.reportDate) - new Date(a.reportDate);
    });
  };

  return (
    <div className="reports-admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>OTR Reports Administration</Title>
        <Space>
          <Button 
            danger
            icon={<DeleteOutlined />}
            onClick={showDeleteAllConfirm}
            disabled={!Array.isArray(reports) || reports.length === 0}
          >
            Delete All
          </Button>
          <Button 
            type="primary" 
            icon={<UploadOutlined />}
            onClick={() => {
              resetUploadForm();
              setShowUploadModal(true);
            }}
          >
            Upload New Report
          </Button>
        </Space>
      </div>

      <Card>
        <Spin spinning={isLoading || isDeletingAll || uploading}>
          <Table 
            dataSource={getSortedReports()} 
            columns={columns}
            rowKey="reportId"
            pagination={{ pageSize: 10 }}
          />
        </Spin>
      </Card>

      {/* Upload Modal */}
      <Modal
        title="Upload OTR Reports"
        open={showUploadModal}
        onCancel={() => setShowUploadModal(false)}
        width={600}
        footer={[
          <Button key="back" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={uploading} 
            onClick={handleUpload}
            disabled={fileList.length === 0}
          >
            Upload {fileList.length > 0 ? `(${fileList.length} Files)` : ''}
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="Report Type">
            <Select 
              value={reportType} 
              onChange={setReportType}
              style={{ width: '100%' }}
            >
              {Array.isArray(reportTypes) && reportTypes.map(type => (
                <Option key={type.id} value={type.id}>{type.name}</Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item>
            <Alert
              message="Multiple File Upload"
              description={
                <div>
                  <p>Each file will be uploaded as a separate report. The report date will be extracted from each filename if available (format YYYYMMDD).</p>
                  <p>If a date can't be found in a filename, the current date will be used.</p>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          </Form.Item>
          
          <Form.Item label="Upload Files">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag files to this area to upload</p>
              <p className="ant-upload-hint">
                Support for CSV, Excel and TXT files - Dates will be extracted from filenames
              </p>
            </Dragger>
          </Form.Item>
          
          {uploading && (
            <Form.Item>
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <Text type="secondary">Processing {fileUploadProgress.current} of {fileUploadProgress.total} files</Text>
              </div>
              <Progress 
                percent={Math.round((fileUploadProgress.current / (fileUploadProgress.total || 1)) * 100)?.toFixed(2)} 
                status="active" 
                showInfo
              />
            </Form.Item>
          )}
          
          {!uploading && uploadResults.length > 0 && (
            <Form.Item label="Upload Results">
              <div style={{ marginBottom: 8 }}>
                <Text strong>
                  {uploadResults.filter(r => r.success).length} of {uploadResults.length} files uploaded successfully
                </Text>
              </div>
              <Table
                dataSource={uploadResults}
                rowKey="file"
                size="small"
                pagination={{ pageSize: 10 }}
                columns={[
                  {
                    title: 'Filename',
                    dataIndex: 'file',
                    key: 'file',
                  },
                  {
                    title: 'Status',
                    key: 'status',
                    render: (_, record) => (
                      <Tag color={record.success ? 'success' : 'error'}>
                        {record.success ? 'Success' : 'Failed'}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Details',
                    key: 'details',
                    render: (_, record) => (
                      record.success ? 
                        <span>Report ID: {record.result?.reportId?.substring(0, 8) || 'Unknown'}</span> : 
                        <span style={{ color: 'red' }}>{record.error}</span>
                    )
                  }
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
